import { FC } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Broker, SignOff } from "../../../../api/Api";
import { brokerKeys, useUpdateBroker } from "../../dataHooks/brokerQueries";
import { SignOffPageLayout } from "../../../../components/lifecycle/signOff/signOffPage";
import { SignOffDetailsPanel } from "./signOffDetailsPanel";
import { buildSignOffPdf } from "./buildSignOffPdf";

type Props = {
  specificBroker: Broker;
};

export const SignOffPage: FC<Props> = ({ specificBroker }) => {
  const updateBroker = useUpdateBroker();
  const queryClient = useQueryClient();

  const persist = (
    signOff: SignOff,
    handlers: { onError: (e: Error) => void; onSuccess: () => void }
  ) => {
    updateBroker.mutate(
      { ...specificBroker, signOff },
      {
        onError: (e) => handlers.onError(e as Error),
        onSuccess: () => {
          handlers.onSuccess();
          // The backend may have mutated the sign-off (e.g. flipping status to "completed" once
          // all signatories are signed). The shared useUpdateBroker hook seeds the cache with the
          // request body, so without an explicit invalidation we'd keep showing our optimistic
          // state until the user reloads.
          if (specificBroker.id) {
            queryClient.invalidateQueries({
              queryKey: brokerKeys.specific(specificBroker.id)
            });
          }
        }
      }
    );
  };

  const handleDownloadPdf = (displayedSignOff?: SignOff | null) => {
    const doc = buildSignOffPdf({
      ...specificBroker,
      signOff: displayedSignOff ?? specificBroker.signOff
    });
    const safeName = (specificBroker.brokerName || "broker").replace(
      /[^a-z0-9]/gi,
      "_"
    );
    doc.save(`signoff_${safeName}.pdf`);
  };

  return (
    <SignOffPageLayout
      entityName={specificBroker.brokerName}
      signOff={specificBroker.signOff}
      vp={specificBroker.vp}
      saving={updateBroker.isPending}
      persist={persist}
      detailsPanel={(extraSections) => (
        <SignOffDetailsPanel
          broker={specificBroker}
          extraSections={extraSections}
        />
      )}
      onDownloadPdf={handleDownloadPdf}
      withOperationalReadiness
    />
  );
};
